import React, { useState } from 'react'
import { Menu, MenuItem, Position } from '@blueprintjs/core'
import { Button, Flex, Popover } from '@ui'
import LocationSearch from './Search'
import useLocations from './useLocations'
import { IUIButtonProps } from '@ui/buttons/Button'
import { CityColumn, Building } from './helperComponents'
import { get } from 'lodash'
import { Mode } from './types'
import { IBuildingDetails, IPortfolioCity, IPortfolioRegion } from '@api'
import css from './locationSelect.module.scss'

interface MultiRegionCityBuilidingSelectProps {
  includeOldBuildings?: boolean
  mode: Mode
  values: Array<IPortfolioRegion | IPortfolioCity | IBuildingDetails>
  buttonProps?: IUIButtonProps
  onChange: (value: any) => void
}

export function multiRegionCityBuilidingSelector(
  item: IPortfolioRegion | IPortfolioCity | IBuildingDetails,
  values: Array<IPortfolioRegion | IPortfolioCity | IBuildingDetails>
) {
  const newValues: Array<IPortfolioRegion | IPortfolioCity | IBuildingDetails> = []

  // remove item from values if it is already in values
  if (values.some((v) => v.id === item.id)) {
    return values.filter((i) => i.id !== item.id)
  }

  if ('cities' in item) {
    // Handle region select
    values.forEach((value) => {
      if ('buildings' in value && item.cities.some((city) => city.id === value.id)) {
        // Remove all previously selected cities that belong to this region
        return
      } else if (
        'floors' in value &&
        !!item.cities.find((city) => city.buildings.some((building) => building.id === value.id))
      ) {
        // Remove all previously selected buildings that belong to this region
        return
      }
      newValues.push(value)
    })
    newValues.push(item)
  } else if ('buildings' in item) {
    // Handle city selection
    let addItem = true
    values.forEach((value) => {
      if ('cities' in value && value.cities.some((city) => city.id === item.id)) {
        addItem = false
        // Remove region and add all cities except item
        value.cities.forEach((city) => {
          if (city !== item) {
            newValues.push(city)
          }
        })
      } else if ('floors' in value && item.buildings.find((building) => building.id === value.id)) {
        // Remove all previously selected buildings that belong to this city
        return
      } else {
        newValues.push(value)
      }
    })

    addItem && newValues.push(item)
  } else if ('floors' in item) {
    // Handle building selection
    let addItem = true
    values.forEach((value) => {
      if (
        'cities' in value &&
        value.cities.find((city) => city.buildings.find((building) => building.id === item.id))
      ) {
        // Remove region and add all cities and buildings except item
        addItem = false
        value.cities.forEach((city) => {
          if (city.buildings.some((building) => building.id === item.id)) {
            city.buildings.forEach((building) => {
              building !== item && newValues.push(building)
            })
          } else {
            newValues.push(city)
          }
        })
      } else if (
        'buildings' in value &&
        value.buildings.some((building) => building.id === item.id)
      ) {
        addItem = false
        // Remove city and add all buildings except item
        value.buildings.forEach((building) => {
          building !== item && newValues.push(building)
        })
      } else {
        newValues.push(value)
      }
    })

    addItem && newValues.push(item)
  }
  return newValues
}

/**
 * This is a helper component which should not be used directly.
 * Use @ui/selectes/MultiLocationSelect instead.
 */
export default function MultiRegionCityBuilidingSelect({
  mode,
  values,
  buttonProps = {},
  onChange,
  includeOldBuildings,
}: MultiRegionCityBuilidingSelectProps) {
  const regions = useLocations(includeOldBuildings)
  const [cityNav, setCityNav] = useState<IPortfolioCity | null>(null)
  const buildings: IBuildingDetails[] = get(cityNav, 'buildings', [])
  let parentSelected = false
  if (cityNav) {
    parentSelected = !!values.find((value) => {
      return (
        ('buildings' in value && value.id === cityNav.id) ||
        ('cities' in value && value.cities.some((city) => city.id === cityNav.id))
      )
    })
  }

  const onSelect = (item: IPortfolioRegion | IPortfolioCity | IBuildingDetails) => {
    const newValues = multiRegionCityBuilidingSelector(item, values)

    onChange(newValues)
  }

  return (
    <Popover
      minimal
      usePortal
      position={Position.BOTTOM}
      content={
        <div className={css.container}>
          <LocationSearch
            mode={mode}
            regions={regions}
            selectCity={(city) => {
              mode !== Mode.MULTI_REGION_CITY && setCityNav(city)
              onSelect(city)
            }}
            selectBuilding={(building) => {
              onSelect(building)
            }}
            selectFloor={() => null}
          />
          <Flex>
            <CityColumn
              multi
              regions={regions}
              cityNav={cityNav}
              setCityNav={(city) => mode !== Mode.MULTI_REGION_CITY && setCityNav(city)}
              onSelect={onSelect}
              isSelected={(item) => values.some((v) => v.id === item.id)}
            />
            {mode !== Mode.MULTI_REGION_CITY && (
              <div className={css.col} style={{ minWidth: 300 }}>
                <div className={css.col_header} style={{ paddingLeft: 11 }}>
                  buildings
                </div>
                {buildings.length ? (
                  <div className={css.list}>
                    <Menu>
                      {buildings.map((b) => (
                        <div key={b.id} style={{ borderBottom: '1px solid #eee' }}>
                          <MenuItem
                            onClick={(e) => {
                              e.preventDefault()
                              onSelect(b)
                            }}
                            shouldDismissPopover={false}
                            text={
                              <Building
                                mode={Mode.MULTI_BUILDING}
                                building={b}
                                selected={parentSelected || values.some((v) => v.id === b.id)}
                              />
                            }
                          />
                        </div>
                      ))}
                    </Menu>
                  </div>
                ) : (
                  <Menu>
                    <MenuItem disabled text="Select a city to see buildings" />
                  </Menu>
                )}
              </div>
            )}
          </Flex>
        </div>
      }
    >
      <Button {...buttonProps} />
    </Popover>
  )
}
